import jwt from '../../http/requests/auth/jwt/index.js'
import router from '@/router'
import axios from '@/axios.js'

// Every login path (email/password, OTP, SSO hand-off, tenant selection) runs
// through processToken. UPDATE_USER_INFO / SET_ACTIVE_TENANT MERGE onto whatever
// is already in localStorage, so a leftover session from a DIFFERENT account
// leaks its fields into the new one (e.g. Console "Open in SmartInvoice" while
// another tenant is logged in). Wipe the previous session first so each login
// fully REPLACES it; the setters below re-establish the new account's values.
function clearSessionStorage() {
  const sessionKeys = [
    'accessToken', 'tokenExpiry', 'loggedIn', 'userInfo',
    'tenantData', 'tenantName', 'tenantSlug', 'maxUserCount',
    'isSP', 'readOnlySession', 'permissions', 'lastActivity',
    'pendingTenants', 'pendingEmail',
  ]
  sessionKeys.forEach((key) => localStorage.removeItem(key))
}

async function processToken(loginToken, commit) {
  try {
    clearSessionStorage()
    const { access_token: accessToken, expires_in: expiresInSeconds } =
      loginToken
    localStorage.setItem('accessToken', accessToken)
    localStorage.setItem(
      'tokenExpiry',
      Date.now() + Number(expiresInSeconds) * 1000
    )
    localStorage.setItem('loggedIn', true)

    const base64Url = accessToken.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        })
        .join('')
    )
    const userInfo = JSON.parse(jsonPayload)

    // Read-only sessions come from the SmartInvoice Console "Open in SmartInvoice"
    // hand-off (the token carries a `readOnly` claim). Normal logins clear the flag.
    // The server enforces the write-ban; this only drives the read-only banner/UX.
    localStorage.setItem('readOnlySession', userInfo.readOnly ? 'true' : 'false')

    // Set bearer token in axios
    commit('SET_BEARER', accessToken)
    // Update user details
    commit('UPDATE_USER_INFO', userInfo, { root: true })
    // Fetch current user data
    const { data: userData } = await axios.get('/tenant-users/current')
    if (userData) {
      localStorage.setItem('isSP', userData.isSalesPerson)
      if (userData.profilePicture) {
        userData.photoURL = userData.profilePicture
      }
      commit('UPDATE_USER_INFO', userData, {
        root: true,
      })
    }

    const { data: tenantData } = await axios.get('tenants/activeTenant')
    if (tenantData) {
      localStorage.setItem('maxUserCount', tenantData.maxUserCount)
      localStorage.setItem('tenantSlug', tenantData.slug)
      await commit('SET_ACTIVE_TENANT', tenantData, { root: true })
    }

    // Clear any pending tenant data after successful login
    localStorage.removeItem('pendingTenants')
    localStorage.removeItem('pendingEmail')

    return router
      .push(router.currentRoute.query.to || '/dashboard')
      .catch(() => {})
  } catch (error) {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('tokenExpiry')
    localStorage.removeItem('loggedIn')
    localStorage.removeItem('userInfo')
    localStorage.removeItem('pendingTenants')
    localStorage.removeItem('pendingEmail')
    localStorage.removeItem('readOnlySession')
    throw error
  }
}

export default {
  async login({ commit }, payload) {
    const { username, password } = payload.userDetails
    try {
      const { data } = await jwt.login(username, password)
      if (!data) {
        throw new Error('Wrong Email or Password')
      }
      return await processToken(data, commit)
    } catch (error) {
      // Handle SSO user attempting password login
      if (
        error.response?.status === 500 &&
        error.response?.data?.message?.includes('Illegal arguments') &&
        error.response?.data?.errors?.[0]?.details?.stack?.includes('bcrypt')
      ) {
        // This indicates an SSO user tried to login with password
        const ssoError = new Error(
          'This account uses SSO authentication. Please use the SSO login option instead.'
        )
        ssoError.isSsoUser = true
        ssoError.originalError = error
        throw ssoError
      }

      // Handle other specific error cases
      if (error.response?.status === 400) {
        throw new Error(
          'Invalid email or password. Please check your credentials.'
        )
      }

      if (error.response?.status === 404) {
        throw new Error('Account not found. Please contact your administrator.')
      }

      if (error.response?.status === 403) {
        throw new Error(
          'Account is disabled. Please contact your administrator.'
        )
      }

      // For any other errors, preserve the original error
      throw error
    }
  },
  async resendOtp({ commit }, payload) {
    const { username, password } = payload
    try {
      const { data } = await jwt.resendOtp(username, password)
      if (!data) {
        throw new Error('Unable to resend OTP')
      }
      return data
    } catch (error) {
      throw error
    }
  },
  async validateOtp({ commit }, payload) {
    const { username, otpCode } = payload
    try {
      const { data } = await jwt.validateOtp(username, otpCode)
      if (!data) {
        throw new Error('Invalid OTP')
      }
      return await processToken(data, commit)
    } catch (error) {
      throw error
    }
  },
  fetchAccessToken() {
    return new Promise((resolve) => {
      jwt.refreshToken().then((response) => {
        resolve(response)
      })
    })
  },
  async checkSsoStatus({ commit }, payload) {
    const { email } = payload
    try {
      const response = await jwt.checkSsoStatus(email)
      return response
    } catch (error) {
      throw error
    }
  },
  async validateSamlSession({ commit }, payload) {
    const { sessionToken } = payload
    try {
      const { data } = await jwt.validateSamlSession(sessionToken)
      if (!data || !data.availableTenants) {
        throw new Error('Invalid session or no tenants available')
      }
      return data
    } catch (error) {
      throw error
    }
  },
  async selectTenant({ commit }, payload) {
    const { sessionToken, tenantId } = payload
    try {
      const { data } = await jwt.selectTenant(sessionToken, tenantId)
      if (!data) {
        throw new Error('Unable to select tenant')
      }
      return await processToken(data, commit)
    } catch (error) {
      throw error
    }
  },
  async completeSsoWithTenant({ commit }, payload) {
    const { sessionToken, tenantId } = payload
    try {
      const { data } = await jwt.completeSsoWithTenant(sessionToken, tenantId)
      if (!data) {
        throw new Error('Unable to complete SSO')
      }
      return await processToken(data, commit)
    } catch (error) {
      throw error
    }
  },
  async processSsoLogin({ commit }, tokenData) {
    try {
      return await processToken(tokenData, commit)
    } catch (error) {
      throw error
    }
  },
}
